import React, { useState, useEffect } from 'react';
import { convertCurrency } from '../../common/getExchangeRate';
import { formatCurrency } from '../utils/currency';

interface ConvertedAmountProps {
  amount: number;
  currency: string;
  targetCurrency: string;
  className?: string;
}

export const ConvertedAmount: React.FC<ConvertedAmountProps> = ({
  amount,
  currency,
  targetCurrency,
  className = '',
}) => {
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currency === targetCurrency) {
      setConvertedAmount(amount);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    convertCurrency(amount, currency, targetCurrency)
      .then((converted) => {
        setConvertedAmount(converted.round(2).toNumber());
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to convert currency');
        setIsLoading(false);
      });
  }, [amount, currency, targetCurrency]);

  const originalAmountText = formatCurrency(amount, currency);
  const showTooltip = currency !== targetCurrency;

  return (
    <span className={className}>
      {isLoading && (
        <span className="inline-block">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 inline-block"></div>
        </span>
      )}
      {!isLoading && !error && convertedAmount !== null && (
        <span
          className={showTooltip ? "border-b border-dotted border-gray-600 cursor-help" : ""}
          title={showTooltip ? originalAmountText : undefined}
        >
          {formatCurrency(convertedAmount, targetCurrency)}
        </span>
      )}
      {!isLoading && error && (
        <span className="text-red-500 text-sm">
          {error}
        </span>
      )}
    </span>
  );
};
